/**
 * Cross-Tab Session Synchronization
 * 
 * Ensures auth state is synchronized across all browser tabs using:
 * 1. BroadcastChannel API (modern browsers)
 * 2. localStorage events (fallback)
 * 3. Periodic sync checks (safety net)
 */

import { getSessionFromLocalStorage, clearSessionFromLocalStorage } from './sessionManager';

export type SessionSyncEvent = 
  | { type: 'login'; sessionId: string; user: any; company: any }
  | { type: 'logout' }
  | { type: 'session_expired' }
  | { type: 'session_updated'; user: any; company: any }
  | { type: 'ping' }
  | { type: 'pong' };

export type SessionChangeHandler = (event: SessionSyncEvent) => void;

class CrossTabSessionSync {
  private broadcastChannel: BroadcastChannel | null = null;
  private handlers: Set<SessionChangeHandler> = new Set();
  private lastKnownSessionId: string | null = null;
  private syncInterval: number | null = null;
  private storageKey = 'pathkit_session_sync';
  
  constructor() {
    this.init();
  }

  private init() {
    // Try to use BroadcastChannel API (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel('pathkit_session_sync');
        this.broadcastChannel.onmessage = (event) => {
          this.handleMessage(event.data);
        };
        console.log('[CrossTabSync] Using BroadcastChannel API');
      } catch (error) {
        console.warn('[CrossTabSync] BroadcastChannel not available, using localStorage fallback');
      }
    }

    // Listen for localStorage changes (fallback method)
    window.addEventListener('storage', this.handleStorageEvent);

    // Periodic sync check (safety net every 5 seconds)
    this.syncInterval = window.setInterval(() => {
      this.checkSessionChanges();
    }, 5000);

    // Check session on mount
    const { sessionId } = getSessionFromLocalStorage();
    this.lastKnownSessionId = sessionId;

    console.log('[CrossTabSync] Initialized');
  }

  private handleStorageEvent = (event: StorageEvent) => {
    // Only handle our sync key
    if (event.key === this.storageKey) {
      try {
        const data = event.newValue ? JSON.parse(event.newValue) : null;
        if (data) {
          this.handleMessage(data);
        }
      } catch (error) {
        console.error('[CrossTabSync] Error parsing storage event:', error);
      }
    }

    // Also check for direct session changes
    if (event.key?.includes('_session_id')) {
      this.checkSessionChanges();
    }
  };

  private handleMessage(event: SessionSyncEvent) {
    console.log('[CrossTabSync] Received message:', event.type);
    
    // Notify all handlers
    this.handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('[CrossTabSync] Error in handler:', error);
      }
    });

    // Update last known session
    if (event.type === 'login') {
      this.lastKnownSessionId = event.sessionId;
    } else if (event.type === 'logout' || event.type === 'session_expired') {
      this.lastKnownSessionId = null;
    }
  }

  private checkSessionChanges() {
    const { sessionId, user, company } = getSessionFromLocalStorage();
    
    // Session was removed (logout in another tab)
    if (this.lastKnownSessionId && !sessionId) {
      console.log('[CrossTabSync] Session removed, broadcasting logout');
      this.lastKnownSessionId = null;
      this.handleMessage({ type: 'logout' });
    }
    
    // New session detected (login in another tab)
    else if (!this.lastKnownSessionId && sessionId) {
      console.log('[CrossTabSync] New session detected, broadcasting login');
      this.lastKnownSessionId = sessionId;
      this.handleMessage({ type: 'login', sessionId, user, company });
    }
    
    // Session ID changed (new login)
    else if (this.lastKnownSessionId && sessionId && this.lastKnownSessionId !== sessionId) {
      console.log('[CrossTabSync] Session ID changed, broadcasting new login');
      this.lastKnownSessionId = sessionId;
      this.handleMessage({ type: 'login', sessionId, user, company });
    }
  }

  /**
   * Broadcast a session event to all tabs
   */
  public broadcast(event: SessionSyncEvent) {
    console.log('[CrossTabSync] Broadcasting event:', event.type);

    // Send via BroadcastChannel if available
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
      } catch (error) {
        console.error('[CrossTabSync] Error broadcasting via BroadcastChannel:', error);
      }
    }

    // Also write to localStorage for fallback support
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        ...event,
        timestamp: Date.now()
      }));
      // Clear it after a short delay so other tabs can read it
      setTimeout(() => {
        const current = localStorage.getItem(this.storageKey);
        if (current) {
          const parsed = JSON.parse(current);
          // Only clear if it's the same message (prevent race conditions)
          if (parsed.timestamp === event.timestamp) {
            localStorage.removeItem(this.storageKey);
          }
        }
      }, 100);
    } catch (error) {
      console.error('[CrossTabSync] Error broadcasting via localStorage:', error);
    }
  }

  /**
   * Subscribe to session change events
   */
  public subscribe(handler: SessionChangeHandler): () => void {
    this.handlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Notify all tabs about login
   */
  public notifyLogin(sessionId: string, user: any, company: any) {
    this.broadcast({ type: 'login', sessionId, user, company });
  }

  /**
   * Notify all tabs about logout
   */
  public notifyLogout() {
    this.broadcast({ type: 'logout' });
  }

  /**
   * Notify all tabs about session expiration
   */
  public notifySessionExpired() {
    this.broadcast({ type: 'session_expired' });
  }

  /**
   * Notify all tabs about session update (e.g., user profile changed)
   */
  public notifySessionUpdated(user: any, company: any) {
    this.broadcast({ type: 'session_updated', user, company });
  }

  /**
   * Check if there are other active tabs
   */
  public async pingOtherTabs(): Promise<boolean> {
    return new Promise((resolve) => {
      let responded = false;
      
      const handler = (event: SessionSyncEvent) => {
        if (event.type === 'pong') {
          responded = true;
        }
      };
      
      // Listen for pong
      const unsubscribe = this.subscribe(handler);
      
      // Send ping
      this.broadcast({ type: 'ping' });
      
      // Wait for responses
      setTimeout(() => {
        unsubscribe();
        resolve(responded);
      }, 200);
    });
  }

  /**
   * Respond to pings from other tabs
   */
  private setupPingPong() {
    this.subscribe((event) => {
      if (event.type === 'ping') {
        // Respond with pong
        this.broadcast({ type: 'pong' });
      }
    });
  }

  /**
   * Clean up resources
   */
  public destroy() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    
    window.removeEventListener('storage', this.handleStorageEvent);
    
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
    this.handlers.clear();
    
    console.log('[CrossTabSync] Destroyed');
  }
}

// Singleton instance
let syncInstance: CrossTabSessionSync | null = null;

/**
 * Get the global cross-tab sync instance
 */
export function getCrossTabSync(): CrossTabSessionSync {
  if (!syncInstance) {
    syncInstance = new CrossTabSessionSync();
    // Setup ping-pong responder
    syncInstance['setupPingPong']();
  }
  return syncInstance;
}

/**
 * React hook for cross-tab session sync
 */
export function useCrossTabSessionSync(
  onLogin?: (sessionId: string, user: any, company: any) => void,
  onLogout?: () => void,
  onSessionExpired?: () => void,
  onSessionUpdated?: (user: any, company: any) => void
) {
  const sync = getCrossTabSync();
  
  React.useEffect(() => {
    const handler: SessionChangeHandler = (event) => {
      switch (event.type) {
        case 'login':
          onLogin?.(event.sessionId, event.user, event.company);
          break;
        case 'logout':
          onLogout?.();
          break;
        case 'session_expired':
          onSessionExpired?.();
          break;
        case 'session_updated':
          onSessionUpdated?.(event.user, event.company);
          break;
      }
    };
    
    const unsubscribe = sync.subscribe(handler);
    
    return () => {
      unsubscribe();
    };
  }, [onLogin, onLogout, onSessionExpired, onSessionUpdated]);
  
  return sync;
}

// Export for use in React components
import React from 'react';
